import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ANALYTICS_EVENTS } from "analytics";
import { useAnalytics } from "hooks/useAnalytics";
import { applyAppearanceToDocument, resolveAppearanceMode } from "./apply-appearance";
import { applySolarCycleBlendToDocument } from "./apply-solar-cycle-blend";
import {
  appearancePreferencesEqual,
  hasMeaningfulAppearance,
  isDefaultAppearance,
  normalizeAppearancePreferences,
  readLegacyAppearanceStorage,
  readStoredAppearance,
  writeStoredAppearance,
} from "./storage";
import type { SolarCycleBlend, SolarPhase } from "./solar-cycle";
import { resolveSolarCycleMode } from "./solar-cycle";
import {
  appearanceValueForPreference,
  buildAppearancePreferencesChangedProperties,
  syncAppearancePosthogProperties,
  type AppearancePreferenceKey,
} from "./track-appearance-analytics";
import { useSolarCycleAppearance } from "./useSolarCycleAppearance";
import { useUserPreferences } from "providers/user-preferences";
import { queueUserPreferencesPatch } from "providers/user-preferences/storage";
import type {
  AppearanceFont,
  AppearanceFontSize,
  AppearanceMode,
  AppearancePreferences,
  AppearanceSource,
  AppearanceTheme,
  ResolvedAppearanceMode,
} from "./types";

type AppearanceContextValue = {
  source: AppearanceSource;
  mode: AppearanceMode;
  theme: AppearanceTheme;
  resolvedTheme: AppearanceTheme;
  font: AppearanceFont;
  fontSize: AppearanceFontSize;
  resolvedMode: ResolvedAppearanceMode;
  solarPhase: SolarPhase | null;
  solarPhaseLabel: string | null;
  solarBlend: SolarCycleBlend | null;
  setSource: (source: AppearanceSource) => void;
  setMode: (mode: AppearanceMode) => void;
  setTheme: (theme: AppearanceTheme) => void;
  setFont: (font: AppearanceFont) => void;
  setFontSize: (fontSize: AppearanceFontSize) => void;
};

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const { trackEvent } = useAnalytics();
  const {
    preferences: userPreferences,
    patchPreferences,
    isHydrated,
    userId,
  } = useUserPreferences();
  const seededAppearanceRef = useRef(false);
  const appearanceSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [preferences, setPreferences] = useState<AppearancePreferences>(() =>
    readStoredAppearance()
  );
  const [prefersDark, setPrefersDark] = useState<boolean>(() => getSystemPrefersDark());
  const canApplyAppearance = !userId || isHydrated;
  const isSolar = preferences.source === "solar";
  const { resolution: solarResolution } = useSolarCycleAppearance({
    enabled: isSolar,
  });

  const resolvedMode = useMemo(() => {
    if (isSolar) {
      return resolveSolarCycleMode(solarResolution);
    }

    return resolveAppearanceMode(preferences.mode, prefersDark);
  }, [isSolar, preferences.mode, prefersDark, solarResolution]);

  const effectiveTheme = isSolar ? solarResolution.theme : preferences.theme;

  useEffect(() => {
    if (!canApplyAppearance) {
      return;
    }

    applyAppearanceToDocument(resolvedMode, {
      theme: effectiveTheme,
      font: preferences.font,
      fontSize: preferences.fontSize,
    });

    if (isSolar) {
      applySolarCycleBlendToDocument(solarResolution.blend);
      return;
    }

    applySolarCycleBlendToDocument(null);
  }, [
    canApplyAppearance,
    effectiveTheme,
    isSolar,
    preferences.font,
    preferences.fontSize,
    resolvedMode,
    solarResolution.blend,
  ]);

  useEffect(() => {
    if (!hasMeaningfulAppearance(userPreferences.appearance)) {
      return;
    }

    const next = normalizeAppearancePreferences(userPreferences.appearance);
    setPreferences((current) => (appearancePreferencesEqual(current, next) ? current : next));
  }, [userPreferences.appearance]);

  useEffect(() => {
    seededAppearanceRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (!userId || !isHydrated || seededAppearanceRef.current) {
      return;
    }

    seededAppearanceRef.current = true;

    if (hasMeaningfulAppearance(userPreferences.appearance)) {
      return;
    }

    const legacy = readLegacyAppearanceStorage();
    if (legacy && !isDefaultAppearance(legacy)) {
      patchPreferences({ appearance: legacy });
      return;
    }

    const local = readStoredAppearance();
    if (!isDefaultAppearance(local)) {
      patchPreferences({ appearance: local });
    }
  }, [isHydrated, patchPreferences, userId, userPreferences.appearance]);

  useEffect(
    () => () => {
      if (appearanceSyncTimeoutRef.current) {
        clearTimeout(appearanceSyncTimeoutRef.current);
      }
    },
    [userId]
  );

  useEffect(() => {
    syncAppearancePosthogProperties(preferences, resolvedMode, {
      solarPhase: isSolar ? solarResolution.phase : null,
      resolvedTheme: effectiveTheme,
    });
  }, [effectiveTheme, isSolar, preferences, resolvedMode, solarResolution.phase]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersDark(event.matches);
    };

    setPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const persistPreferences = useCallback(
    (next: AppearancePreferences) => {
      setPreferences(next);
      if (userId) {
        queueUserPreferencesPatch(userId, { appearance: next });
        if (appearanceSyncTimeoutRef.current) {
          clearTimeout(appearanceSyncTimeoutRef.current);
        }
        appearanceSyncTimeoutRef.current = setTimeout(() => {
          patchPreferences({ appearance: next });
        }, 250);
      } else {
        writeStoredAppearance(next);
      }
    },
    [patchPreferences, userId]
  );

  const trackPreferenceChange = useCallback(
    (preference: AppearancePreferenceKey, next: AppearancePreferences) => {
      const previousValue = appearanceValueForPreference(preferences, preference);
      const nextValue = appearanceValueForPreference(next, preference);

      if (previousValue === nextValue) {
        return;
      }

      const properties = buildAppearancePreferencesChangedProperties(
        preference,
        nextValue,
        previousValue,
        next,
        prefersDark,
        next.source === "solar" ? solarResolution : null
      );

      trackEvent(ANALYTICS_EVENTS.APPEARANCE_PREFERENCES_CHANGED, { ...properties });
      syncAppearancePosthogProperties(next, properties.resolved_mode as ResolvedAppearanceMode, {
        solarPhase: next.source === "solar" ? solarResolution.phase : null,
        resolvedTheme: properties.resolved_theme as AppearanceTheme,
      });
    },
    [preferences, prefersDark, solarResolution, trackEvent]
  );

  const setSource = useCallback(
    (source: AppearanceSource) => {
      if (source === preferences.source) {
        return;
      }

      if (source === "solar") {
        const next: AppearancePreferences = {
          ...preferences,
          source: "solar",
          savedManual: {
            mode: preferences.mode,
            theme: preferences.theme,
          },
        };
        persistPreferences(next);
        trackPreferenceChange("source", next);
        return;
      }

      const restored = preferences.savedManual ?? {
        mode: preferences.mode,
        theme: preferences.theme,
      };
      const next: AppearancePreferences = {
        ...preferences,
        source: "manual",
        mode: restored.mode,
        theme: restored.theme,
        savedManual: undefined,
      };
      persistPreferences(next);
      trackPreferenceChange("source", next);
    },
    [persistPreferences, preferences, trackPreferenceChange]
  );

  const setMode = useCallback(
    (mode: AppearanceMode) => {
      const next = { ...preferences, mode };
      persistPreferences(next);
      trackPreferenceChange("mode", next);
    },
    [persistPreferences, preferences, trackPreferenceChange]
  );

  const setTheme = useCallback(
    (theme: AppearanceTheme) => {
      const next = { ...preferences, theme };
      persistPreferences(next);
      trackPreferenceChange("theme", next);
    },
    [persistPreferences, preferences, trackPreferenceChange]
  );

  const setFont = useCallback(
    (font: AppearanceFont) => {
      const next = { ...preferences, font };
      persistPreferences(next);
      trackPreferenceChange("font", next);
    },
    [persistPreferences, preferences, trackPreferenceChange]
  );

  const setFontSize = useCallback(
    (fontSize: AppearanceFontSize) => {
      const next = { ...preferences, fontSize };
      persistPreferences(next);
      trackPreferenceChange("font_size", next);
    },
    [persistPreferences, preferences, trackPreferenceChange]
  );

  const value = useMemo(
    () => ({
      source: preferences.source,
      mode: preferences.mode,
      theme: preferences.theme,
      resolvedTheme: effectiveTheme,
      font: preferences.font,
      fontSize: preferences.fontSize,
      resolvedMode,
      solarPhase: isSolar ? solarResolution.phase : null,
      solarPhaseLabel: isSolar ? solarResolution.label : null,
      solarBlend: isSolar ? solarResolution.blend : null,
      setSource,
      setMode,
      setTheme,
      setFont,
      setFontSize,
    }),
    [
      isSolar,
      preferences.font,
      preferences.fontSize,
      preferences.mode,
      preferences.source,
      preferences.theme,
      effectiveTheme,
      resolvedMode,
      setFont,
      setFontSize,
      setMode,
      setSource,
      setTheme,
      solarResolution.blend,
      solarResolution.label,
      solarResolution.phase,
    ]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance(): AppearanceContextValue {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error("useAppearance must be used within AppearanceProvider");
  }

  return context;
}
